<template>
  <div ref="popupRoot" class="lisaui:relative">
    <LISAButton
      variant="neutral"
      size="sm"
      icon="fas fa-filter"
      full-height
      :aria-expanded="showFilterPopover"
      aria-haspopup="dialog"
      @click="open()"
    >
      <span class="filter-button-label">{{ t(buttonL10nKey, numberOf) }}</span>
      <LISABadge variant="neutral" class="ml-1">{{ numberOf }}x</LISABadge>
      <template #append>
        <i class="fas fa-chevron-down lisaui:text-xs" />
      </template>
    </LISAButton>

    <div
      v-if="showFilterPopover"
      class="filter-popup"
      :class="`${accent}-popup`"
      :style="{ '--filter-popup-max-height': popupMaxHeight }"
    >
      <FilterList
        ref="filterList"
        :filter-config="filterConfig"
        :types="types"
        :site-id="siteId"
        :title="title"
        :accent="accent"
        :event-filters="eventFilters"
        :action-filters="actionFilters"
        @apply="applyFilters"
        @close="close"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";
import { nextTick, onMounted, onUnmounted, type PropType, ref } from "vue";
import { EABFilterType } from "@/graphql";
import { LISABadge, LISAButton } from "@fi-solutions/lisa-ui";
import FilterList from "./FilterList.vue";
import { Filter, type FilterConfig, type FilterEventActionTypes } from "./FilterTypes";

const props = defineProps({
  filterConfig: {
    required: true,
    type: Array as PropType<FilterConfig[]>,
  },
  filters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  eventFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  actionFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  filterType: {
    type: String as PropType<EABFilterType>,
    default: EABFilterType.AND,
  },
  buttonL10nKey: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  accent: {
    type: String as PropType<"event" | "action">,
    required: true,
  },
  numberOf: {
    type: Number,
    required: true,
  },
  types: {
    type: Array as PropType<FilterEventActionTypes[]>,
    default: () => [],
  },
  showArchive: {
    type: Boolean,
    default: false,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const emit = defineEmits<{
  apply: [Filter[], EABFilterType];
}>();
const filterList = ref<InstanceType<typeof FilterList>>();
const popupRoot = ref<HTMLElement>();
const showFilterPopover = ref(false);
const { t } = useTranslation();
const popupMaxHeight = ref("calc(100dvh - 12rem)");
const NEWSBAR_SAFE_AREA_PX = 61;
const POPUP_GAP_PX = 12;
const MIN_POPUP_HEIGHT_PX = 260;

async function open(focusFilter?: Filter) {
  showFilterPopover.value = true;
  await nextTick();
  updatePopupMaxHeight();
  await filterList.value?.initialize(props.filters, props.filterType, focusFilter);
}

function loadFilter(filter: Filter) {
  open(filter);
}

function close() {
  showFilterPopover.value = false;
}

function updatePopupMaxHeight() {
  if (popupRoot.value == null) {
    return;
  }
  const triggerBottom = popupRoot.value.getBoundingClientRect().bottom;
  const availableHeight = window.innerHeight - triggerBottom - POPUP_GAP_PX - NEWSBAR_SAFE_AREA_PX;
  popupMaxHeight.value = `${Math.max(MIN_POPUP_HEIGHT_PX, Math.floor(availableHeight))}px`;
}

function applyFilters(filters: Filter[], type: EABFilterType) {
  emit("apply", filters, type);
  close();
}

defineExpose({
  loadFilter,
  open,
});

function closeOnEscape(event: KeyboardEvent) {
  if (event.key === "Escape" && showFilterPopover.value) {
    close();
  }
}

function closeOnOutsideClick(event: PointerEvent) {
  const target = event.target;
  if (
    target instanceof Element &&
    target.closest(
      ".p-select-overlay, .p-multiselect-overlay, .p-popover, .p-menu, .p-dialog, .p-dialog-mask, .dp__menu, .dp__outer_menu_wrap",
    )
  ) {
    return;
  }
  if (showFilterPopover.value && target instanceof Node && !popupRoot.value?.contains(target)) {
    close();
  }
}

onMounted(() => {
  window.addEventListener("keyup", closeOnEscape);
  window.addEventListener("pointerdown", closeOnOutsideClick);
  window.addEventListener("resize", updatePopupMaxHeight);
});
onUnmounted(() => {
  window.removeEventListener("keyup", closeOnEscape);
  window.removeEventListener("pointerdown", closeOnOutsideClick);
  window.removeEventListener("resize", updatePopupMaxHeight);
});
</script>

<style lang="scss" scoped>
.filter-popup {
  position: absolute;
  z-index: 100;
  top: calc(100% + 0.75rem);
  width: min(980px, calc(100vw - 4rem));
  max-width: calc(100vw - 1rem);
  max-height: min(720px, var(--filter-popup-max-height, calc(100dvh - 12rem)));
  overflow: hidden;
  border: 1px solid var(--p-gray-300);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
  box-shadow: 0 20px 50px rgb(0 0 0 / 20%);
}

.filter-popup::before {
  position: absolute;
  z-index: 1;
  top: -0.45rem;
  width: 0.8rem;
  height: 0.8rem;
  border-top: 1px solid var(--p-gray-300);
  border-left: 1px solid var(--p-gray-300);
  background: var(--p-surface-0);
  content: "";
  transform: rotate(45deg);
}

.event-popup {
  left: 0;
}

.event-popup::before {
  left: 2rem;
}

.action-popup {
  right: 0;
}

.action-popup::before {
  right: 2rem;
}

@media (max-width: 36rem) {
  .filter-button-label {
    display: none;
  }
}
</style>
